import {
  CreateDealerDemoAccountPayload,
  DemoAccount,
  DownloadCsvSearchParams,
  GetPartnerUsersResponse,
  InvitedUser,
  InvitePartnerResponse,
  InviteUserResponse,
  Partner,
  PartnersResponse,
  PartnersSearchParams,
  PartnerTiersResponse,
  PartnerUser,
  PaymentSource,
  UserInvitePayload,
} from '~/types/partners'
import { PaymentMethod } from '~/types/subscriptions'
import { ApiKy } from './index'
import { InvoicesResponse, PartnerInvoicesSearchParams } from '~/types/invoices'
import {
  CreditNotesResponse,
  PartnerCreditNotesSearchParams,
} from '~/types/credit-notes'
import { SearchParams } from '~/types/pagination'

const { stripEmptyKeys } = useHelpers()

export default class Api extends ApiKy {
  get(searchParams: PartnersSearchParams) {
    const params = stripEmptyKeys(searchParams)
    return this.ky
      .get(this.url, { searchParams: params })
      ?.json<PartnersResponse>()
  }

  delete(partnerId: string) {
    return this.ky.delete(`${this.url}/${partnerId}`)
  }

  getSingle(partnerId: string) {
    return this.ky.get(`${this.url}/${partnerId}`)?.json<Partner>()
  }

  getDealerDemoAccounts(partnerId: string, searchParams: SearchParams = {}) {
    const params = stripEmptyKeys(searchParams)
    return this.ky
      .get(`${this.url}/${partnerId}/demo-accounts`, { searchParams: params })
      ?.json<DemoAccount[]>()
  }

  createDealerDemoAccount(
    partnerId: string,
    payload: CreateDealerDemoAccountPayload,
  ) {
    return this.ky.post(`${this.url}/${partnerId}/demo-accounts`, {
      json: payload,
    })
  }

  resendDealerDemoInvites(partnerId: string, demoId: string) {
    return this.ky.post(
      `${this.url}/${partnerId}/demo-accounts/${demoId}/resend-invites`,
    )
  }

  addDealerDemoInviteEmails(partnerId: string, demoId: string, emails: string) {
    return this.ky.post(
      `${this.url}/${partnerId}/demo-accounts/${demoId}/add-invite-emails`,
      { json: { emails } },
    )
  }

  getUsers(partnerId: string, searchParams: PartnerInvoicesSearchParams) {
    const params = stripEmptyKeys(searchParams)
    return this.ky
      .get(`${this.url}/${partnerId}/users`, { searchParams: params })
      ?.json<GetPartnerUsersResponse>()
  }

  getPaymentSource(partnerId: string) {
    return this.ky
      .get(`${this.url}/${partnerId}/payment-sources`)
      ?.json<PaymentSource[]>()
  }

  updatePaymentSource(
    partnerId: string,
    payload: {
      paymentMethod: PaymentMethod
      braintreePaymentToken: string
    },
  ) {
    return this.ky
      .put(`${this.url}/${partnerId}/payment-sources`, { json: payload })
      ?.json<PaymentSource>()
  }

  updateSingle(partnerId: string, payload: Partial<Partner>) {
    return this.ky
      .patch(`${this.url}/${partnerId}`, { json: payload })
      ?.json<Partner>()
  }

  getShortlist(countryCode: string) {
    const query = countryCode ? `?country=${countryCode}` : ''
    return this.ky.get(`${this.url}/shortlist${query}`)?.json<Partner[]>()
  }

  // TO-DO: add types for responses
  invitePartner(payload: Partial<Partner>) {
    return this.ky
      .post(`${this.url}/invite`, { json: payload })
      ?.json<InvitePartnerResponse>()
  }

  inviteUser(partnerId: string, payload: UserInvitePayload) {
    return this.ky
      .post(`${this.url}/${partnerId}/users/invite`, { json: payload })
      ?.json<InviteUserResponse>()
  }

  getInvoices(partnerId: string, searchParams: PartnerInvoicesSearchParams) {
    const params = stripEmptyKeys(searchParams)
    return this.ky
      .get(`${this.url}/${partnerId}/invoices`, { searchParams: params })
      ?.json<InvoicesResponse>()
  }

  getCreditNotes(
    partnerId: string,
    searchParams: PartnerCreditNotesSearchParams,
  ) {
    const params = stripEmptyKeys(searchParams)
    return this.ky
      .get(`${this.url}/${partnerId}/credit-notes`, { searchParams: params })
      ?.json<CreditNotesResponse>()
  }

  getInvoiceUrl(partnerId: string, invoiceId: string) {
    return this.ky
      .get(`${this.url}/${partnerId}/invoices/${invoiceId}/pdf`)
      ?.json<{ downloadUrl: string }>()
  }

  claimUser(partnerUserId: string) {
    return this.ky.post(`${this.url}/users/${partnerUserId}/claim`)
  }

  claimPartner(partnerId: string, payload: Partial<Partner>) {
    return this.ky.post(`${this.url}/${partnerId}/claim`, { json: payload })
  }

  deleteUser(partnerId: string, userId: string) {
    return this.ky.delete(`${this.url}/${partnerId}/users/${userId}`)
  }

  editUser(
    partnerId: string,
    userId: string,
    payload: { firstName: string; lastName: string },
  ) {
    return this.ky
      .patch(`${this.url}/${partnerId}/users/${userId}`, { json: payload })
      ?.json<PartnerUser>()
  }

  resendTeamMemberInvite(partnerId: string, userId: string) {
    return this.ky
      .post(`${this.url}/${partnerId}/users/${userId}/resend-invite`)
      ?.json<InvitedUser>()
  }

  resendInvite(partnerId: string) {
    return this.ky
      .post(`${this.url}/${partnerId}/resend-invite`)
      ?.json<InvitePartnerResponse>()
  }

  downloadCsv(searchParams: DownloadCsvSearchParams) {
    const params = stripEmptyKeys(searchParams)
    return this.ky.get(`${this.url}/download-csv`, {
      searchParams: params,
    })
  }

  checkSalesForceCustomerId(payload: {
    salesforceCustomerId: string
    partnerId?: string
  }) {
    return this.ky.post(`${this.url}/check-salesforce-customer-id`, {
      json: payload,
    })
  }

  mergePartners(fromPartnerId: string, toPartnerId: string) {
    return this.ky.post(`${this.url}/merge-partners`, {
      json: { fromPartnerId, toPartnerId },
    })
  }

  getPartnerTiers() {
    return this.ky.get(`${this.url}/tiers`)?.json<PartnerTiersResponse[]>()
  }
}
